// Standard library imports
import React, { useState, useEffect } from 'react';
// External library imports
import { toast } from 'react-toastify';
import { UilSync, UilEye, UilPen } from '@iconscout/react-unicons';
// Internal module imports
import Table from '../../../../components/Table/Table';
import Typography from '../../../../components/Typography/Typography';
import { useLoader } from '../../../../hooks';
import mfrrClasses from '../index.module.css';
import { ToggleButtonWithState } from '../../../../components/Inputs/Input';
import CustomTooltip from '../../../../components/CustomToolTip/CustomTooltip';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import _ from 'lodash';
import { DropdownComponent } from '../../../../components/Inputs/Input';
import { RootMFRRService } from '../../../../services/RootMFRRService';
import UpdateRedisValue from './ModalComponent/UpdateRedisValue';

import classes from '../../../../styles/AllDevices.module.css';
import tableClasses from '../../../../components/Table/Table.module.css';

const RedisViewer = ({ queryParamsData, updateQueryParams, selectedEicCode, setSelectedEicCode, eicCodeOptions }) => {
    const [startLoader, stopLoader] = useLoader();
    const [data, setData] = useState([]);
    const [isAutoRefresh, setIsAutorefresh] = useState(false);
    const [seconds, setSeconds] = useState(10);
    const [counterInterval, setCounterInterval] = useState(null);
    const [updateRedisValueModal, setUpdateRedisValueModal] = useState({
        status: false,
        data: {},
        action: '',
    });

    useEffect(() => {
        if (selectedEicCode?.value) getRedisData();
    }, [selectedEicCode]);

    const getRedisData = (showLoader = true) => {
        const params = {
            eicCode: selectedEicCode.value,
        };
        RootMFRRService.GetRedisData(params, showLoader ? startLoader : () => { }, handleSuccess, handleError, showLoader ? stopLoader : () => { });
    };

    const handleSuccess = ({ data }) => {
        const processedData = data.data.map((item, index) => {
            return {
                sno: index + 1,
                key: item.key,
                timestamp: _.get(item, ['value', 'timestamp'], '--'),
                action: (
                    <div className={tableClasses.ActionContainer} >
                        <CustomTooltip content={'View'}>
                            <UilEye
                                onClick={() => {
                                    setUpdateRedisValueModal({
                                        status: true,
                                        data: {
                                            key: item.key,
                                            eicCode: selectedEicCode?.value,
                                            redisData: item.value,
                                        },
                                        action: 'view'
                                    });
                                }}
                                style={{ color: 'var(--color-primary)' }}
                                size={'1.2vw'}
                            />
                        </CustomTooltip>
                        <CustomTooltip content={'Update'}>
                            <UilPen
                                onClick={() => {
                                    setUpdateRedisValueModal({
                                        status: true,
                                        data: {
                                            key: item.key,
                                            eicCode: selectedEicCode?.value,
                                            redisData: item.value,
                                        },
                                        action: 'update'
                                    });
                                }}
                                style={{ color: 'var(--color-primary)' }}
                                size={'1.2vw'}
                            />
                        </CustomTooltip>
                    </div>
                ),
            };
        });

        setData(processedData);
    };

    const handleError = (err) => {
        console.log(err);
        if (err && err.response) toast.error(err.response.data.message);
    };

    useEffect(() => {
        if (isAutoRefresh) {
            const interval = setInterval(() => {
                if (seconds > 0) {
                    setSeconds(seconds - 1);
                } else {
                    getRedisData(false);
                    setSeconds(10);
                }
            }, 1000);
            setCounterInterval(interval);
            return () => clearInterval(interval);
        }
    }, [isAutoRefresh, seconds]);

    return (
        <div>
            <ModalComponent
                isOpen={updateRedisValueModal.status}
                setOpen={() => {
                    setUpdateRedisValueModal({ status: false, data: {}, action: '' });
                }}
            >
                <UpdateRedisValue
                    data={updateRedisValueModal.data}
                    action={updateRedisValueModal.action}
                    setOpen={setUpdateRedisValueModal}
                    fetchValues={getRedisData}
                />
            </ModalComponent>
            <div className={mfrrClasses.Filter}>
                <div className={mfrrClasses.OuterContainer}>
                    <div className={mfrrClasses.FilterContainer}>
                        <div>
                            <div className={classes.FieldControl}>
                                <label>Select EIC Code</label>
                                <DropdownComponent
                                    name="eicCode"
                                    options={eicCodeOptions}
                                    defaultValue={selectedEicCode}
                                    onChange={(e) => {
                                        if (e.value !== selectedEicCode?.value) {
                                            setSelectedEicCode(e);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={mfrrClasses.FilterContainer}>
                        <div className={mfrrClasses.RefreshContainer}>
                            <div>
                                <CustomTooltip content={'Refresh'}>
                                    <div>
                                        <UilSync
                                            size={'1.5vw'}
                                            style={{
                                                cursor: 'pointer',
                                                marginTop: '0.4vw',
                                                color: 'var(--color-primary)',
                                            }}
                                            onClick={getRedisData}
                                        />
                                    </div>
                                </CustomTooltip>
                            </div>
                            <div className={mfrrClasses.TimerContainer}>
                                <ToggleButtonWithState
                                    label={'Auto Refresh'}
                                    value={isAutoRefresh}
                                    style={{ marginTop: '0' }}
                                    onChange={(e) => {
                                        setIsAutorefresh(e.target.checked);
                                        clearInterval(counterInterval);
                                        setSeconds(10);
                                    }}
                                />
                                {isAutoRefresh && <label className={mfrrClasses.TimerLabel}>in {seconds} seconds</label>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.AllDevices}>
                <div className={classes.Header}>
                    <div>
                        <Typography content="Redis Console" />
                        <div className={classes.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={data.length || 0} />
                            </span>
                        </div>
                    </div>
                </div>
                <div>
                    <div>
                        <Table
                            head={['S.No', 'Key', 'Timestamp', 'Action']}
                            keys={['sno', 'key', 'timestamp', 'action']}
                            data={data}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RedisViewer;
